import { useState, useEffect } from 'react';
import { View, Text, ScrollView, FlatList, Image, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import { useLocalSearchParams, useNavigation } from 'expo-router';
import { useProgress } from '@/hooks/useProgress';
import { useWorkoutPerformance } from '@/hooks/useWorkoutPerformance';
import { ProgressChart } from '@/components/ProgressChart';
import { ExerciseProgressCard } from '@/components/ExerciseProgressCard';
import { ExerciseProgressModal } from '@/components/ExerciseProgressModal';
import { supabase } from '@/lib/supabase';
import { ExerciseHistory } from '@/lib/types';

type Tab = 'performance' | 'body';

export default function PersonalProgressView() {
  const { studentId } = useLocalSearchParams<{ studentId: string }>();
  const navigation = useNavigation();
  const [activeTab, setActiveTab] = useState<Tab>('performance');
  const [selectedExercise, setSelectedExercise] = useState<ExerciseHistory | null>(null);
  const [studentFirstName, setStudentFirstName] = useState('');

  const { records, photos, loading: progressLoading } = useProgress(studentId);
  const { history, loading: perfLoading } = useWorkoutPerformance(studentId);

  useEffect(() => {
    if (!studentId) return;
    supabase.from('students').select('full_name, email').eq('id', studentId).single()
      .then(({ data }) => {
        if (data) {
          const first = (data.full_name ?? data.email ?? '').split(' ')[0];
          navigation.setOptions({ title: `Progresso — ${first}` });
          setStudentFirstName(first);
        }
      });
  }, [studentId]);

  if (progressLoading || perfLoading) return <ActivityIndicator style={{ flex: 1 }} color="#C8102E" />;
  if (!studentId) return <Text style={{ margin: 20, color: '#888' }}>Aluno não encontrado.</Text>;

  const weightData = records.map(r => ({
    value: r.weight_kg,
    label: new Date(r.recorded_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
  }));

  const fatData = records
    .filter(r => r.body_fat_pct !== null)
    .map(r => ({
      value: r.body_fat_pct as number,
      label: new Date(r.recorded_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
    }));

  const latestRecord = records[records.length - 1];
  const firstRecord = records[0];
  const weightDelta = latestRecord && firstRecord && records.length > 1
    ? +(latestRecord.weight_kg - firstRecord.weight_kg).toFixed(1) : null;

  const measurementKeys = ['waist', 'arm', 'hip', 'thigh'] as const;
  const measurementLabels: Record<string, string> = { waist: 'Cintura', arm: 'Braço', hip: 'Quadril', thigh: 'Coxa' };
  const recordsWithMeasurements = records.filter(r => r.measurements && Object.keys(r.measurements).length > 0);
  const latestMeasurements = recordsWithMeasurements[recordsWithMeasurements.length - 1]?.measurements;
  const firstMeasurements = recordsWithMeasurements[0]?.measurements;

  const totalDays = firstRecord && latestRecord && records.length > 1
    ? Math.round((new Date(latestRecord.recorded_at).getTime() - new Date(firstRecord.recorded_at).getTime()) / (1000 * 60 * 60 * 24))
    : null;
  const waistDelta = firstMeasurements?.waist !== undefined && latestMeasurements?.waist !== undefined && recordsWithMeasurements.length > 1
    ? +(latestMeasurements.waist - firstMeasurements.waist).toFixed(1) : null;
  const insightParts: string[] = [];
  if (weightDelta !== null) insightParts.push(weightDelta < 0 ? `perdeu ${Math.abs(weightDelta).toFixed(1)}kg` : `ganhou ${Math.abs(weightDelta).toFixed(1)}kg`);
  if (waistDelta !== null) insightParts.push(waistDelta < 0 ? `${Math.abs(waistDelta).toFixed(1)}cm de cintura` : `+${Math.abs(waistDelta).toFixed(1)}cm de cintura`);
  const subject = studentFirstName || 'o aluno';
  const insightText = totalDays && insightParts.length > 0 ? `Em ${totalDays} dias, ${subject} ${insightParts.join(' e ')}.` : null;

  const grouped = history.reduce<Record<string, ExerciseHistory[]>>((acc, h) => {
    if (!acc[h.muscleGroup]) acc[h.muscleGroup] = [];
    acc[h.muscleGroup].push(h);
    return acc;
  }, {});

  return (
    <View style={styles.container}>
      <View style={styles.tabBar}>
        {([['performance', 'Desempenho'], ['body', 'Evolução Corporal']] as const).map(([key, label]) => (
          <TouchableOpacity key={key} style={[styles.tab, activeTab === key && styles.tabActive]} onPress={() => setActiveTab(key)}>
            <Text style={[styles.tabText, activeTab === key && styles.tabTextActive]}>{label}</Text>
          </TouchableOpacity>
        ))}
      </View>

      {activeTab === 'performance' ? (
        <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 40 }}>
          {history.length === 0 ? (
            <View style={styles.emptyState}>
              <Text style={styles.emptyText}>Este aluno ainda não completou nenhum treino.</Text>
            </View>
          ) : (
            Object.entries(grouped).map(([muscleGroup, exercises]) => (
              <View key={muscleGroup}>
                <Text style={styles.groupHeader}>{muscleGroup}</Text>
                {exercises.map(h => (
                  <ExerciseProgressCard key={h.exerciseId} history={h} onPress={() => setSelectedExercise(h)} />
                ))}
              </View>
            ))
          )}
        </ScrollView>
      ) : (
        <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 40 }}>
          {insightText && (
            <View style={styles.insightCard}>
              <Text style={styles.insightText}>{insightText}</Text>
            </View>
          )}
          {latestRecord && (
            <View style={styles.highlightCard}>
              <Text style={styles.highlightLabel}>Peso atual</Text>
              <Text style={styles.highlightValue}>{latestRecord.weight_kg} kg</Text>
              {weightDelta !== null && (
                <Text style={[styles.highlightDelta, { color: weightDelta <= 0 ? '#c8e6c9' : 'rgba(255,255,255,0.9)' }]}>
                  {weightDelta > 0 ? `+${weightDelta}` : weightDelta} kg desde o início
                </Text>
              )}
              {latestRecord.body_fat_pct && (
                <Text style={styles.highlightFat}>{latestRecord.body_fat_pct}% gordura</Text>
              )}
            </View>
          )}
          <ProgressChart data={weightData} title="Peso (kg)" />
          {fatData.length >= 2 && <ProgressChart data={fatData} title="% Gordura" />}
          {latestMeasurements && (
            <View style={styles.measureTable}>
              <Text style={styles.sectionTitle}>Medidas</Text>
              {measurementKeys.filter(k => latestMeasurements[k] !== undefined).map(k => {
                const latest = latestMeasurements[k];
                const first = firstMeasurements?.[k];
                const delta = first !== undefined && latest !== undefined && recordsWithMeasurements.length > 1
                  ? +(latest - first).toFixed(1) : null;
                return (
                  <View key={k} style={styles.measureRow}>
                    <Text style={styles.measureLabel}>{measurementLabels[k]}</Text>
                    <Text style={styles.measureValue}>{latest} cm</Text>
                    {delta !== null && (
                      <Text style={[styles.measureDelta, { color: delta <= 0 ? '#2E7D32' : '#C8102E' }]}>
                        {delta > 0 ? `+${delta}` : delta}
                      </Text>
                    )}
                  </View>
                );
              })}
            </View>
          )}
          {photos.length > 0 && (
            <View>
              <Text style={styles.sectionTitle}>Fotos</Text>
              <FlatList
                data={photos}
                keyExtractor={p => p.id}
                numColumns={2}
                scrollEnabled={false}
                renderItem={({ item }) => <Image source={{ uri: item.photo_url }} style={styles.photo} />}
                contentContainerStyle={{ gap: 8 }}
                columnWrapperStyle={{ gap: 8 }}
              />
            </View>
          )}
          {!latestRecord && photos.length === 0 && (
            <View style={styles.emptyState}>
              <Text style={styles.emptyText}>Nenhum registro de evolução corporal ainda.</Text>
            </View>
          )}
        </ScrollView>
      )}

      <ExerciseProgressModal history={selectedExercise} visible={!!selectedExercise} onClose={() => setSelectedExercise(null)} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F5F5F5' },
  tabBar: { flexDirection: 'row', backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#eee' },
  tab: { flex: 1, paddingVertical: 14, alignItems: 'center' },
  tabActive: { borderBottomWidth: 2, borderBottomColor: '#C8102E' },
  tabText: { fontSize: 13, fontWeight: '600', color: '#aaa' },
  tabTextActive: { color: '#C8102E' },
  emptyState: { padding: 32, alignItems: 'center' },
  emptyText: { color: '#aaa', textAlign: 'center', fontSize: 14, lineHeight: 22 },
  groupHeader: { fontSize: 12, fontWeight: '700', color: '#888', letterSpacing: 0.5, marginTop: 12, marginBottom: 6, textTransform: 'uppercase' },
  highlightCard: { backgroundColor: '#C8102E', borderRadius: 16, padding: 20, marginBottom: 16, alignItems: 'center' },
  highlightLabel: { color: 'rgba(255,255,255,0.8)', fontSize: 13 },
  highlightValue: { color: '#fff', fontSize: 40, fontWeight: 'bold', marginTop: 4 },
  highlightDelta: { fontSize: 13, fontWeight: '600', marginTop: 6 },
  highlightFat: { color: 'rgba(255,255,255,0.8)', fontSize: 13, marginTop: 4 },
  sectionTitle: { fontSize: 14, fontWeight: '700', color: '#1A1A1A', marginBottom: 10, marginTop: 8 },
  measureTable: { backgroundColor: '#fff', borderRadius: 10, padding: 16, marginBottom: 16 },
  measureRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#f0f0f0' },
  measureLabel: { flex: 1, fontSize: 14, color: '#555' },
  measureValue: { fontSize: 14, fontWeight: '600', color: '#1A1A1A', marginRight: 12 },
  measureDelta: { fontSize: 12, fontWeight: '600', minWidth: 40, textAlign: 'right' },
  photo: { flex: 1, aspectRatio: 1, borderRadius: 8, backgroundColor: '#eee' },
  insightCard: { backgroundColor: '#e8f5e9', borderRadius: 12, padding: 16, marginBottom: 12, alignItems: 'center' },
  insightText: { color: '#2E7D32', fontSize: 14, fontWeight: '600', textAlign: 'center', lineHeight: 20 },
});
